import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Alert, Anchor } from "@mantine/core";
import { useAtomValue } from "jotai";

import { useExtensionGateRecheck, useExtensionStore } from "@prostocheck/data-web";
import { isExtensionGateConnectedAtom } from "@prostocheck/shared";

/**
 * The login page's notice of whether the jkurwa-web gate browser extension is
 * connected — the "show a notice at login" half of the ask, next to
 * `ExtensionGateBadge` for every other screen. On a browser we do not publish
 * the extension for, it says so instead of pointing at the install guide.
 * See ai/docs/kep-signing.md.
 *
 * `useExtensionGateRecheck` re-probes on mount whenever the last answer was
 * "not connected", so a user who installs the extension via the CTA below and
 * comes back to `/login` gets a fresh check instead of the stale banner.
 */
export const ExtensionGateAlert = () => {
  const { t } = useTranslation();
  const isConnected = useAtomValue(isExtensionGateConnectedAtom);
  const { isSupported } = useExtensionStore();

  useExtensionGateRecheck();

  // Still checking, right after app start — render nothing rather than flash a state.
  if (isConnected === null) return null;

  if (isConnected) {
    return (
      <Alert
        color="green"
        variant="light"
        radius="md"
        mb="md"
        title={t("extensionGate.login.connectedTitle")}
      >
        {t("extensionGate.login.connectedDescription")}
      </Alert>
    );
  }

  if (!isSupported) {
    return (
      <Alert
        color="blue"
        variant="light"
        radius="md"
        mb="md"
        title={t("extensionGate.login.notConnectedTitle")}
      >
        {t("extensionGate.login.unsupportedBrowser")}
      </Alert>
    );
  }

  return (
    <Alert
      color="blue"
      variant="light"
      radius="md"
      mb="md"
      title={t("extensionGate.login.notConnectedTitle")}
    >
      {t("extensionGate.login.notConnectedDescription")}{" "}
      <Anchor component={Link} to="/extension" size="sm" fw={500}>
        {t("extensionGate.login.cta")}
      </Anchor>
    </Alert>
  );
};
